import { mapValues } from "lodash-es";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";
import { i18n } from "@/i18n";
import { showConfirm } from "@core/ui/alert/alert";

import type { ActionItem } from "./ActionItem";
import type { EffectItem } from "@editor/items/effect/EffectItem";

import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useRequirementStore } from "../requirement/requirements.store";
import type { ActionCostItem } from "./ActionCostItem";

/**
 * Action with all dependencies loaded (effects and conditions)
 */

export const actionItemStructure: ActionItem = {
  type: "action",
  id: 0,
  uid: "action_0",

  title: "",
  description: "",
  costs: [], // { [resourceId]: pointsToDeduct }
  maxOccurrences: null,
  imageId: null,

  effectsVisibility: "as-is",

  hiddenIfCostsNotCovered: false,
  hiddenIfRequirementsNotFulfilled: false,
  hiddenIfMaxUsesReached: true,

  docs: {},
  order: -1,
  createdAt: -1
};

export const useActionStore = defineItemStore(
  "model/actions",
  actionItemStructure,
  {
    getters: {
      byTarget(state): { [targetId: number]: ActionItem[] } {
        // get the effects affecting the target
        const effectStore = useEffectStore();
        const targetEffects = effectStore.byTarget;
        const allActions = state.items;

        // return the actions associated with the effects
        return mapValues(targetEffects, (effects) => {
          const actionEffects = effects.filter(
            (effect): effect is EffectItem & { activatorId: number } =>
              effect.activatorId != null && effect.activatorType === "action"
          );
          const actions = actionEffects.map(
            (effect) => allActions[effect.activatorId]
          );
          return actions.filter((action) => action != null).sort(this.sortFunction);
        });
      },
      byResource() {
        // get the effects affecting the resource
        const effectStore = useEffectStore();
        const resourceEffects = effectStore.byResource;
        const allActions: { [actionId: number]: ActionItem } = this.byId;

        // return the actions associated with the effects
        return mapValues(resourceEffects, (effects) => {
          const actionEffects = effects.filter(
            (effect): effect is EffectItem & { activatorId: number } =>
              effect.activatorType === "action"
          );
          return actionEffects.map(
            (effect) => allActions[effect.activatorId]
          );
        });
      }
    },
    events: {
      async created(action) {
        useScenarioStore().connectAllToAction({ actionId: action.id });
      },
      async canBeDeleted(action) {
        const simulationStore = useSimulationStore();
        const simulationsWithAction = simulationStore.orderedItems.filter(
          (sim) => Object.values(sim.actionPlans).some((plan) => plan.plannedActionIds.includes(action.id))
        );
        if (simulationsWithAction.length === 0) return true;
        return await showConfirm(
          i18n.t("questions.deleteAction", { amount: simulationsWithAction.length }),
          i18n.t("actions.deleteAction"),
          i18n.t("labels.abort")
        );
      },
      afterDelete(action) {
        const promises = [];

        // delete all effects that are connected with this action
        const effectStore = useEffectStore();
        const effects = effectStore.byAction[action.id];
        if (effects != null) {
          promises.push(effectStore.deleteMany(effects));
        }

        // delete connected IMAGE
        if (action.imageId != null) {
          const imageStore = useImageStore();
          promises.push(
            imageStore.delete({ id: action.imageId, instant: true })
          );
        }

        // remove from scenarios
        const scenarioStore = useScenarioStore();
        const scenarios = scenarioStore.orderedItems.filter((scenario) => {
          return scenario.actionIds.includes(action.id);
        });
        for (const scenario of scenarios) {
          scenarioStore.disconnectAction({
            id: scenario.id,
            actionId: action.id
          });
        }

        // delete all simulations with this action
        const simulationStore = useSimulationStore();
        const simulationsWithAction = simulationStore.orderedItems.filter(
          (sim) => Object.values(sim.actionPlans).some((plan) => plan.plannedActionIds.includes(action.id))
        );
        for (const simulation of simulationsWithAction) {
          const rounds = simulation.rounds;
          if (rounds.some((round) => round.actionCounts?.[action.id])) {
            // delete the simulation if the action is used in the simulation
            promises.push(
              simulationStore.delete({ id: simulation.id, instant: true })
            );
          } else {
            // remove action from action ids
            const actionIds = simulation.actionIds.filter((id) => id !== action.id);
            simulationStore.update({ id: simulation.id, actionIds });
          }
        }

        return Promise.all(promises);
      }
    },
    actions: {
      setCosts({
        id,
        costs
      }: {
        id: number;
        costs: ActionCostItem[]
      }) {
        const action = this.items[id];
        action.costs = costs;
      },
      
      deleteCost({ id, elementUid }: { id: number; elementUid: string }) {
        const action = this.items[id];
        const costs = action.costs.filter((cost) => cost.elementUid !== elementUid);
        return this.update({
          id,
          costs
        });
      },

      async duplicate(id: number, changes: Partial<ActionItem> = {}) {
        const sourceItem = this.items[id];
        const duplicated = await this.super.duplicate(id, {
          imageId: null,
          ...changes
        });

        const effectStore = useEffectStore();
        const effects = effectStore.byAction[id] ?? [];
        for (const effect of effects) {
          await effectStore.duplicate(effect.id, {
            activatorId: duplicated.id
          });
        }

        const requirementStore = useRequirementStore();
        const requirements = requirementStore.byItemUid[`action_${id}`] ?? [];
        for (const requirement of requirements) {
          await requirementStore.duplicate(requirement.id, {
            itemType: "action",
            itemId: duplicated.id
          });
        }

        const imageStore = useImageStore();
        if (sourceItem.imageId != null) {
          const image = imageStore.items[sourceItem.imageId];
          if (image != null) {
            const imageCopy = await imageStore.duplicate(image.id);
            duplicated.imageId = imageCopy.id;
          }
        }

        return duplicated;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useActionStore, import.meta.hot));
}
